import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

const prefersReducedMotion =
  typeof window !== 'undefined' &&
  window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Full-bleed parallax banner. The background image drifts slower than the
 * page (transform-based, rAF-throttled) for a cinematic depth effect, with a
 * slow Ken-Burns zoom layered on top. Falls back to a static cover image when
 * the user prefers reduced motion.
 */
function ParallaxBanner({
  image,
  eyebrow,
  title,
  body,
  ctaLabel,
  ctaTo,
  strength = 90,
}) {
  const sectionRef = useRef(null);
  const bgRef = useRef(null);

  useEffect(() => {
    if (prefersReducedMotion) return;
    const section = sectionRef.current;
    const bg = bgRef.current;
    if (!section || !bg) return;

    let ticking = false;

    const update = () => {
      ticking = false;
      const rect = section.getBoundingClientRect();
      const vh = window.innerHeight || document.documentElement.clientHeight;
      // Skip work when the banner is nowhere near the viewport.
      if (rect.bottom < -vh || rect.top > vh * 2) return;
      // progress: -1 (entering from bottom) -> 0 (centered) -> 1 (leaving top)
      const progress = (rect.top + rect.height / 2 - vh / 2) / (vh + rect.height / 2);
      const shift = -progress * strength;
      bg.style.transform = `translate3d(0, ${shift.toFixed(2)}px, 0) scale(1.18)`;
    };

    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        window.requestAnimationFrame(update);
      }
    };

    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [strength]);

  return (
    <section className="parallax" ref={sectionRef}>
      <div
        className="parallax__bg"
        ref={bgRef}
        style={{ backgroundImage: `url(${image})` }}
        aria-hidden="true"
      />
      <div className="parallax__overlay" aria-hidden="true" />
      <div className="container parallax__content">
        {eyebrow && <span className="parallax__eyebrow">{eyebrow}</span>}
        {title && <h2 className="parallax__title">{title}</h2>}
        {body && <p className="parallax__body">{body}</p>}
        {ctaLabel && ctaTo && (
          <Link to={ctaTo} className="btn btn--primary">
            {ctaLabel}
          </Link>
        )}
      </div>
    </section>
  );
}

export default ParallaxBanner;
